import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Briefcase,
  PhoneCall,
  ShieldCheck,
  Clock,
  CircleDollarSign,
} from "lucide-react";
import { BRAND, IMAGES } from "@/lib/brand";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative bg-white overflow-hidden"
    >
      <div className="absolute inset-0 rk-grid-bg pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 rk-fade-up">
            <div className="inline-flex items-center gap-2 rounded-sm bg-[#1F6B5C]/10 text-[#1F6B5C] px-3 py-1.5 text-xs font-bold tracking-widest uppercase">
              <ShieldCheck className="h-3.5 w-3.5" />
              Chennai · Pan-India Workforce Supply
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0F172A] leading-[1.05]"
            >
              Reliable Manpower
              <br />
              Solutions for{" "}
              <span className="relative inline-block">
                <span className="text-[#1E3A8A]">Modern Industries</span>
                <span className="absolute left-0 right-0 -bottom-1 h-2 bg-[#1F6B5C]/20 -z-10" />
              </span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl"
            >
              {BRAND.name} provides skilled, disciplined, and dependable
              workforce solutions for factories, housekeeping, industrial
              operations, and multiple corporate sectors across India.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                data-testid="hero-cta-hire"
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm h-12 px-6"
              >
                <Link to="/contact?intent=hire">
                  Hire Workforce
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                data-testid="hero-cta-apply"
                variant="outline"
                className="border-2 border-[#1F6B5C] text-[#1F6B5C] hover:bg-[#1F6B5C] hover:text-white rounded-sm h-12 px-6"
              >
                <Link to="/careers">
                  <Briefcase className="mr-2 h-4 w-4" />
                  Apply for Jobs
                </Link>
              </Button>
              <Button
                asChild
                data-testid="hero-cta-contact"
                variant="ghost"
                className="text-[#0F172A] hover:bg-slate-100 rounded-sm h-12 px-6"
              >
                <Link to="/contact">
                  <PhoneCall className="mr-2 h-4 w-4" />
                  Contact Us
                </Link>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg">
              <Stat icon={<ShieldCheck className="h-4 w-4" />} num="500+" label="Workers placed" />
              <Stat icon={<Clock className="h-4 w-4" />} num="24/7" label="Shift staffing" />
              <Stat icon={<CircleDollarSign className="h-4 w-4" />} num="100%" label="PF & ESI compliant" />
            </div>
          </div>

          <div className="lg:col-span-5 relative rk-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] w-full">
              <div className="absolute -top-4 -left-4 h-24 w-24 border-2 border-[#1F6B5C] rounded-sm hidden sm:block" />
              <div className="absolute -bottom-4 -right-4 h-32 w-32 bg-[#1E3A8A]/10 rounded-sm hidden sm:block" />
              <img
                src={IMAGES.factoryWorkers}
                alt="Skilled industrial workforce supplied by RK Enterprises"
                className="relative h-full w-full object-cover rounded-sm shadow-2xl"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur p-4 rounded-sm border border-[#E2E8F0] shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-[#1F6B5C] grid place-items-center">
                    <ShieldCheck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0F172A] text-sm">
                      Statutory compliant deployment
                    </div>
                    <div className="text-xs text-[#64748B]">
                      PF · ESI · Bonus · Increment · OT
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, num, label }) {
  return (
    <div data-testid={`hero-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[#1F6B5C]">
        {icon}
        <span className="font-display text-2xl font-extrabold text-[#0F172A]">
          {num}
        </span>
      </div>
      <div className="text-xs text-[#64748B] mt-1">{label}</div>
    </div>
  );
}
